import { AlertTriangle, ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type {
  BreadthPressureStateDto,
  DebriefBreadthDto,
  DebriefFlowDto,
  DebriefGroupMoveDto,
  DebriefRotationDto,
} from 'shared';

import { StockCandidateList } from '../components/domain/StockCandidateList';
import { SymbolExport } from '../components/ui/SymbolExport';
import { useMarketDebrief } from '../features/marketConsole/queries';
import { formatSignedPct } from '../lib/marketConsoleFormat';
import { useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const PRESSURE_LABELS: Record<BreadthPressureStateDto, string> = {
  BUYING_PRESSURE: 'Buying pressure',
  SELLING_PRESSURE: 'Selling pressure',
  MIXED: 'Mixed',
};

function pressureLabel(state: BreadthPressureStateDto | null): string {
  return state ? PRESSURE_LABELS[state] : 'Unknown';
}

function tone(value: number | null): string {
  if (value === null || value === 0) return 'text-text-muted';
  return value > 0 ? 'text-success' : 'text-danger';
}

function signed(value: number | null, digits = 1, suffix = ''): string {
  if (value === null) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}${suffix}`;
}

function Card({
  id,
  title,
  dates,
  action,
  children,
}: {
  id: string;
  title: string;
  dates: string | null;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface" aria-labelledby={id}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 id={id} className="text-sm font-semibold">
            {title}
          </h2>
          {dates ? <p className="mt-0.5 font-mono text-xs text-text-muted">{dates}</p> : null}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Missing({ children }: { children: ReactNode }) {
  return <p className="text-sm text-text-muted">{children}</p>;
}

function BreadthCard({ breadth }: { breadth: DebriefBreadthDto | null }) {
  const { search } = useLocation();
  return (
    <Card
      id="debrief-breadth"
      title="Breadth"
      dates={breadth ? `${breadth.priorDate} → ${breadth.date}` : null}
      action={
        <Link
          to={{ pathname: '/market-console/pulse', search }}
          className="inline-flex items-center gap-1 text-xs underline"
        >
          Open Pulse <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      }
    >
      {!breadth ? (
        <Missing>Two sessions of breadth are needed. Run the breadth refresh in Settings.</Missing>
      ) : (
        <>
          <p className="text-sm">
            {pressureLabel(breadth.priorPressure)} <span aria-hidden="true">→</span>
            <span className="sr-only">then</span>{' '}
            <span className="font-semibold">{pressureLabel(breadth.pressure)}</span>
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              {
                label: 'Net ±4% movers',
                value: breadth.net4,
                change: breadth.net4Change,
                digits: 0,
              },
              { label: 'T2108', value: breadth.t2108, change: breadth.t2108Change, digits: 1 },
              {
                label: '10D ratio',
                value: breadth.ratio10,
                change: breadth.ratio10Change,
                digits: 2,
              },
            ].map((item) => (
              <div key={item.label}>
                <dt className="text-xs text-text-muted">{item.label}</dt>
                <dd className="font-mono text-sm tabular-nums">
                  {item.value === null ? '—' : item.value.toFixed(item.digits)}{' '}
                  <span className={cn('text-xs', tone(item.change))}>
                    ({signed(item.change, item.digits)})
                  </span>
                </dd>
              </div>
            ))}
            <div>
              <dt className="text-xs text-text-muted">S&amp;P 500</dt>
              <dd className={cn('font-mono text-sm tabular-nums', tone(breadth.spChangePct))}>
                {signed(breadth.spChangePct, 2, '%')}
              </dd>
            </div>
          </dl>
        </>
      )}
    </Card>
  );
}

function MoveList({ title, moves }: { title: string; moves: readonly DebriefGroupMoveDto[] }) {
  return (
    <div className="min-w-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">{title}</h3>
      {moves.length === 0 ? (
        <p className="mt-1 text-sm text-text-muted">None.</p>
      ) : (
        <ol className="mt-1 divide-y divide-border">
          {moves.map((move) => (
            <li key={`${move.universe}-${move.group}`} className="flex items-center gap-2 py-1.5">
              <span className="min-w-0 flex-1 truncate text-sm">{move.group}</span>
              <span className={cn('font-mono text-sm tabular-nums', tone(move.changePct))}>
                {formatSignedPct(move.changePct)}
              </span>
              <span className="w-20 text-right font-mono text-[11px] tabular-nums text-text-muted">
                {move.relativePct === null ? '' : `${signed(move.relativePct, 1)} vs S&P`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function FlowCard({ flow }: { flow: DebriefFlowDto | null }) {
  return (
    <Card
      id="debrief-flow"
      title="Themes and sectors"
      dates={flow ? `${flow.priorDate} → ${flow.date} close to close` : null}
    >
      {!flow ? (
        <Missing>
          Two sessions of ETF data are needed. Run the ETF flow refresh in Settings.
        </Missing>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <MoveList title="Bought (strongest themes)" moves={flow.bought} />
            <MoveList title="Sold (weakest themes)" moves={flow.sold} />
            <MoveList title="Sectors" moves={flow.sectors} />
          </div>
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <p>
              <span className="text-text-muted">Joined the 1-week top 10: </span>
              {flow.enteredTopThemes.length > 0 ? flow.enteredTopThemes.join(', ') : 'none'}
            </p>
            <p>
              <span className="text-text-muted">Left the 1-week top 10: </span>
              {flow.leftTopThemes.length > 0 ? flow.leftTopThemes.join(', ') : 'none'}
            </p>
          </div>
          <p className="text-xs text-text-muted">
            Bought and sold describe price response, not reported fund flows.
          </p>
        </div>
      )}
    </Card>
  );
}

function RotationCard({ rotation }: { rotation: DebriefRotationDto | null }) {
  const follow = rotation?.followThrough ?? null;
  return (
    <Card
      id="debrief-rotation"
      title="Leader rotation"
      dates={rotation ? `${rotation.priorDate} → ${rotation.date}` : null}
      action={
        rotation ? (
          <SymbolExport
            label="new leaders"
            fileName={`new-leaders-${rotation.date}.txt`}
            rows={rotation.entered}
          />
        ) : null
      }
    >
      {!rotation ? (
        <Missing>Two stock scans are needed to compare leaders.</Missing>
      ) : (
        <div className="space-y-4">
          {follow ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Prior leaders today
              </h3>
              <p className="mt-1 text-sm">
                {follow.up} up, {follow.down} down of {follow.count} · median{' '}
                <span className={cn('font-mono tabular-nums', tone(follow.medianChangePct))}>
                  {signed(follow.medianChangePct, 2, '%')}
                </span>
              </p>
              <p className="mt-1 font-mono text-xs tabular-nums text-text-muted">
                Best{' '}
                {follow.best
                  .map((move) => `${move.symbol} ${signed(move.changePct, 1, '%')}`)
                  .join(', ')}
                {' · '}Worst{' '}
                {follow.worst
                  .map((move) => `${move.symbol} ${signed(move.changePct, 1, '%')}`)
                  .join(', ')}
              </p>
            </div>
          ) : null}

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              New non-extended leaders · {rotation.entered.length}
            </h3>
            <div className="-mx-4 mt-1">
              <StockCandidateList
                candidates={rotation.entered}
                horizon={rotation.entered[0]?.topWindows[0] ?? '1M'}
                emptyText="No new leaders since the prior scan."
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                New tight coils
              </h3>
              <p className="mt-1 text-sm">
                {rotation.newTight.length > 0
                  ? rotation.newTight.map((leader) => leader.symbol).join(', ')
                  : 'None.'}
              </p>
            </div>
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Left the list · {rotation.exited.length}
              </h3>
              {rotation.exited.length === 0 ? (
                <p className="mt-1 text-sm">None.</p>
              ) : (
                <ul className="mt-1 space-y-0.5 text-sm">
                  {rotation.exited.map((exit) => (
                    <li key={exit.symbol}>
                      <span className="font-mono font-semibold">{exit.symbol}</span>{' '}
                      <span className="text-text-muted">
                        {exit.reason === 'EXTENDED' ? 'now extended' : 'fell out of the top'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export function MarketDebrief() {
  const { asOf } = useReplayDate();
  const debrief = useMarketDebrief(asOf);

  if (debrief.isLoading) {
    return <main className="p-6 text-text-muted">Loading debrief…</main>;
  }
  if (debrief.isError || !debrief.data) {
    return <main className="p-6 text-danger">The debrief could not be loaded.</main>;
  }
  const { breadth, flow, rotation, status } = debrief.data;

  return (
    <main className="text-text">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-5 sm:px-6">
        <header>
          <h1 className="text-2xl font-semibold tracking-tight">Debrief</h1>
          <p className="mt-1 text-sm text-text-muted">
            What the last completed session changed against the prior close. Informational only.
          </p>
        </header>
        {status === 'NOT_AVAILABLE' ? (
          <section className="flex gap-3 rounded-lg border border-warn/30 bg-warn/10 p-4 text-sm text-warn">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <p>
              Nothing to compare yet. The debrief needs two sessions of breadth, ETF or stock scan
              data; run them in{' '}
              <Link to="/settings/market-data" className="underline">
                Settings → Market data
              </Link>
              .
            </p>
          </section>
        ) : (
          <>
            <BreadthCard breadth={breadth} />
            <FlowCard flow={flow} />
            <RotationCard rotation={rotation} />
          </>
        )}
      </div>
    </main>
  );
}
